import { useState, useEffect } from 'react'
import styles from './PaymentReports.module.css'
import { API_BASE_URL, getImageUrl } from '../config'

interface PaymentReport {
  id: number
  title: string
  slug: string
  thumbnail: string
  logo: string
  industry_category_id: number
  region_category_id: number
  industry_name: string
  region_name: string
  publish_date: string
}

const ArrowIcon = () => (
  <svg className={styles.arrowIcon} viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
    <path d="M885.113 489.373L628.338 232.599c-12.496-12.497-32.758-12.497-45.254 0-12.497 12.497-12.497 32.758 0 45.255l203.3 203.3H158.025c-17.036 0-30.846 13.811-30.846 30.846 0 17.036 13.811 30.846 30.846 30.846h628.36L583.084 746.147c-12.497 12.496-12.497 32.758 0 45.255 6.248 6.248 14.438 9.372 22.627 9.372s16.379-3.124 22.627-9.372l256.775-256.775a31.999 31.999 0 0 0 0-45.254z" fill="#FE6601"/>
  </svg>
)

const SearchIcon = () => (
  <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
    <path d="M16 16l4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

interface PaymentReportsProps {
  onReportClick?: (reportId: number) => void
}

const PaymentReports = ({ onReportClick }: PaymentReportsProps) => {
  const [reports, setReports] = useState<PaymentReport[]>([])
  const [filteredReports, setFilteredReports] = useState<PaymentReport[]>([])
  const [loading, setLoading] = useState(true)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [selectedIndustry, setSelectedIndustry] = useState('全部行业')
  const [selectedRegion, setSelectedRegion] = useState('全部国家和地区')
  const [industryList, setIndustryList] = useState<string[]>(['全部行业'])
  const [regionList, setRegionList] = useState<string[]>(['全部国家和地区'])
  const [parentIndustryMap, setParentIndustryMap] = useState<Map<string, string>>(new Map())
  const [parentRegionMap, setParentRegionMap] = useState<Map<string, string>>(new Map())

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/payment-reports?page_size=100`)
        const result = await response.json()
        if (result.success) {
          setReports(result.data.list || [])
          setFilteredReports(result.data.list || [])
        }
      } catch (error) {
        console.error('Failed to fetch reports:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchReports()
  }, [])

  // 从 API 获取分类列表
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories`)
        const result = await response.json()
        if (result.success) {
          const data = result.data as any[]
          const industries = data.filter((c: any) => c.type === 'industry')
          const regions = data.filter((c: any) => c.type === 'region')

          // 构建子分类→父分类映射
          // 两趟扫描：先建子→父映射，再填充未被映射的父分类自身
          // 确保当同名分类同时存在为父级和子级时，子→父映射优先
          const buildMap = (cats: any[]): Map<string, string> => {
            const map = new Map<string, string>()
            for (const c of cats) {
              for (const child of c.children || []) {
                map.set(child.name, c.name)
              }
            }
            for (const c of cats) {
              if (!map.has(c.name)) {
                map.set(c.name, c.name)
              }
            }
            return map
          }

          const allowedIndustries = new Set(['电商', '数娱', '生成式AI', 'SaaS', '航旅', '在线教育', '零售', '其他'])
          const allowedRegions = new Set(['美国', '欧洲及英国', '东亚', '东南亚', '中东', '拉丁美洲'])

          setParentIndustryMap(buildMap(industries))
          setParentRegionMap(buildMap(regions))
          setIndustryList(['全部行业', ...industries.map((c: any) => c.name).filter((n: string) => allowedIndustries.has(n))])
          setRegionList(['全部国家和地区', ...regions.map((c: any) => c.name).filter((n: string) => allowedRegions.has(n))])
        }
      } catch (error) {
        // 静默失败，使用默认列表
      }
    }
    fetchCategories()
  }, [])

  useEffect(() => {
    let result = reports

    if (searchKeyword) {
      result = result.filter(report =>
        report.title.toLowerCase().includes(searchKeyword.toLowerCase())
      )
    }

    if (selectedIndustry !== '全部行业') {
      result = result.filter(report => {
        const parent = parentIndustryMap.get(report.industry_name)
        return parent === selectedIndustry
      })
    }

    if (selectedRegion !== '全部国家和地区') {
      result = result.filter(report => {
        const parent = parentRegionMap.get(report.region_name)
        return parent === selectedRegion
      })
    }

    setFilteredReports(result)
  }, [searchKeyword, selectedIndustry, selectedRegion, reports, parentIndustryMap, parentRegionMap])

  return (
    <div className={styles.container}>
      {/* Search Field */}
      <div className={styles.searchField}>
        <SearchIcon />
        <input
          type="text"
          className={styles.searchInput}
          placeholder="搜索支付报告……"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
        />
      </div>

      {/* Industry Filters */}
      <div className={styles.filterGroup}>
        <div className={styles.filterTags}>
          {industryList.map((filter) => (
            <div
              key={filter}
              className={`${styles.filterTag} ${selectedIndustry === filter ? styles.active : ''}`}
              onClick={() => setSelectedIndustry(filter)}
            >
              <span>{filter}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Region Filters */}
      <div className={styles.filterGroup}>
        <div className={styles.filterTags}>
          {regionList.map((filter) => (
            <div
              key={filter}
              className={`${styles.filterTag} ${selectedRegion === filter ? styles.active : ''}`}
              onClick={() => setSelectedRegion(filter)}
            >
              <span>{filter}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className={styles.loading}>加载中...</div>
      ) : filteredReports.length === 0 ? (
        <div className={styles.empty}>暂无报告</div>
      ) : (
        filteredReports.map((report) => (
          <div key={report.id} className={styles.reportCard} onClick={() => onReportClick?.(report.id)}>
            <div className={styles.reportThumbnail}>
              {report.logo && <img src={getImageUrl(report.logo)} alt="" />}
            </div>
            <div className={styles.reportContent}>
              <h3 className={styles.reportTitle}>
                {report.title.includes(' - ') ? (
                  <>
                    <span className={styles.reportTitleMain}>{report.title.split(' - ')[0]}</span>
                    <span className={styles.reportTitleSub}>{report.title.split(' - ').slice(1).join(' - ')}</span>
                  </>
                ) : (
                  report.title
                )}
              </h3>
              <div className={styles.reportFooter}>
                <div className={styles.reportArrow}>
                  <ArrowIcon />
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  )
}

export default PaymentReports